















import { Component, Vue } from "vue-property-decorator";

@Component
export default class StickyHScroll extends Vue {
  private resizeObserver: any = null;
  private mutationObserver: any = null;
  private frame = 0;

  private get viewportEl(): HTMLElement | null {
    return (this.$refs.viewport as HTMLElement) || null;
  }

  private get barEl(): HTMLElement | null {
    return (this.$refs.bar as HTMLElement) || null;
  }

  private get spacerEl(): HTMLElement | null {
    return (this.$refs.spacer as HTMLElement) || null;
  }

  mounted(): void {
    this.refresh();
    // Capture phase so the bar also tracks pages that scroll inside a container.
    window.addEventListener("scroll", this.schedule, true);
    window.addEventListener("resize", this.schedule);

    const viewport = this.viewportEl;
    const ResizeObserverCtor = (window as any).ResizeObserver;
    if (ResizeObserverCtor && viewport) {
      this.resizeObserver = new ResizeObserverCtor(this.schedule);
      this.resizeObserver.observe(viewport);
      if (viewport.firstElementChild) {
        this.resizeObserver.observe(viewport.firstElementChild);
      }
    }
    // Row counts change with tabs/search; watch the DOM instead of using
    // updated(), which would re-enter Vue's render cycle from a hook.
    const MutationObserverCtor = (window as any).MutationObserver;
    if (MutationObserverCtor && viewport) {
      this.mutationObserver = new MutationObserverCtor(this.schedule);
      this.mutationObserver.observe(viewport, { childList: true, subtree: true });
    }
  }

  beforeDestroy(): void {
    window.removeEventListener("scroll", this.schedule, true);
    window.removeEventListener("resize", this.schedule);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /** Coalesce bursts of scroll/resize/mutation events into one measure pass. */
  private schedule(): void {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      this.refresh();
    });
  }

  /**
   * Measures the wrapped block and positions the proxy bar. Writes straight to
   * the DOM — no reactive state — so this can never trigger a re-render.
   */
  private refresh(): void {
    const viewport = this.viewportEl;
    const bar = this.barEl;
    const spacer = this.spacerEl;
    if (!viewport || !bar || !spacer) return;

    const rect = viewport.getBoundingClientRect();
    const overflows = viewport.scrollWidth - viewport.clientWidth > 1;
    // Only needed while the block's own scrollbar sits below the fold.
    const ownBarOffScreen = rect.bottom > window.innerHeight;
    const onScreen = rect.top < window.innerHeight && rect.bottom > 0;
    const show = overflows && ownBarOffScreen && onScreen;

    if (!show) {
      if (bar.style.display !== "none") bar.style.display = "none";
      return;
    }

    const left = Math.round(rect.left) + "px";
    const width = Math.round(rect.width) + "px";
    const contentWidth = viewport.scrollWidth + "px";
    if (bar.style.display !== "block") bar.style.display = "block";
    if (bar.style.left !== left) bar.style.left = left;
    if (bar.style.width !== width) bar.style.width = width;
    if (spacer.style.width !== contentWidth) spacer.style.width = contentWidth;
    if (bar.scrollLeft !== viewport.scrollLeft) bar.scrollLeft = viewport.scrollLeft;
  }

  private onViewportScroll(): void {
    const viewport = this.viewportEl;
    const bar = this.barEl;
    if (!viewport || !bar) return;
    if (bar.scrollLeft !== viewport.scrollLeft) bar.scrollLeft = viewport.scrollLeft;
  }

  private onBarScroll(): void {
    const viewport = this.viewportEl;
    const bar = this.barEl;
    if (!viewport || !bar) return;
    if (viewport.scrollLeft !== bar.scrollLeft) viewport.scrollLeft = bar.scrollLeft;
  }
}
